import { Button } from "react-bootstrap"
import { useState } from "react"
import { Link } from "react-router-dom"
import { useGetBuildingAmenitiesQuery, useAddNewBuildingAmenityMutation, useDeleteBuildingAmenityMutation } from "../../../services/bClientApi"
import { useAppDispatch } from "../../../redux/store"
import { showError, showSuccess } from "../../../redux/counter/counterSlice"
import canAccess from "../../../services/accessService/accessService"
import SelectAmenityModal from "../../Modals/SelectAmenityModal/SelectAmenityModal"
import { AMENITY_TAG_OPTIONS, AmenityType } from "../../../Types/amenityTypes"
import useAmenityRefs from "../../../Hooks/UseAmenities/useAmenityRefsHook"
import { BoxArrowUpRight } from "react-bootstrap-icons"

export default function BuildingAmenities(props: {
    buildingId?: string
}) {
    const { buildingId } = props
    const dispatch = useAppDispatch()

    const [selectModalActive, setSelectModalActive] = useState(false)
    const [editMode, setEditMode] = useState(false)

    const { data: buildingAmenitiesData } = useGetBuildingAmenitiesQuery({
        buildingAmenitiesQuery: { buildingId },
        pagination: { page: 1, limit: 500, all: true },
    }, { skip: !buildingId })

    const amenityRef = useAmenityRefs()

    const [addNewBuildingAmenity, { isLoading: isAdding }] = useAddNewBuildingAmenityMutation()
    const [deleteBuildingAmenity, { isLoading: isDeleting }] = useDeleteBuildingAmenityMutation()

    const buildingAmenityList = buildingAmenitiesData?.docs || []

    const assignedAmenityIds = new Set(buildingAmenityList.map((ba: any) => ba.amenityId))

    const sortedBuildingAmenities = [...buildingAmenityList].sort((a: any, b: any) => {
        const nameA = amenityRef[a.amenityId]?.title || ''
        const nameB = amenityRef[b.amenityId]?.title || ''
        return nameA.localeCompare(nameB)
    })

    const tagLabelMap: Record<string, string> = {}
    AMENITY_TAG_OPTIONS.forEach((tagOption) => { tagLabelMap[tagOption.tag] = tagOption.text })

    // amenities with multiple tags appear in each matching group; untagged amenities fall under "Other"
    const groupedByTag = sortedBuildingAmenities.reduce((groups: Record<string, any[]>, ba: any) => {
        const amenity = amenityRef[ba.amenityId]
        const tags = amenity?.tags && amenity.tags.length > 0 ? amenity.tags : ['other']
        tags.forEach((tag: string) => {
            if (!groups[tag]) groups[tag] = []
            groups[tag].push(ba)
        })
        return groups
    }, {})

    const orderedTagKeys = [
        ...AMENITY_TAG_OPTIONS.map((tagOption) => tagOption.tag).filter((tag) => groupedByTag[tag]),
        ...Object.keys(groupedByTag).filter((tag) => tag !== 'other' && !tagLabelMap[tag]).sort(),
        ...(groupedByTag['other'] ? ['other'] : []),
    ]

    const handleAddAmenities = async (amenityData: AmenityType | AmenityType[]) => {
        const amenities = Array.isArray(amenityData) ? amenityData : [amenityData]

        let successCount = 0
        let failCount = 0

        for (const amenity of amenities) {
            try {
                await addNewBuildingAmenity({
                    buildingId,
                    amenityId: amenity._id,
                    overrides: {},
                }).unwrap()
                successCount++
            } catch (err) {
                console.error('Failed to add amenity:', err)
                failCount++
            }
        }

        if (successCount > 0) {
            dispatch(showSuccess(`${successCount} amenit${successCount === 1 ? 'y' : 'ies'} added`))
        }
        if (failCount > 0) {
            dispatch(showError(`Failed to add ${failCount} amenit${failCount === 1 ? 'y' : 'ies'}`))
        }
        setSelectModalActive(false)
    }

    const handleRemoveAmenity = async (buildingAmenityId: string) => {
        try {
            await deleteBuildingAmenity(buildingAmenityId).unwrap()
            dispatch(showSuccess('Amenity removed'))
        } catch (err) {
            console.error('Failed to remove amenity:', err)
            dispatch(showError('Failed to remove amenity'))
        }
    }

    return (
        <>
            {buildingAmenityList.length > 0 && (
                <div className="building-amenities-list">
                    <h4>Building Amenities</h4>

                    <Button
                        className="my-2"
                        size="sm"
                        variant={editMode ? 'danger' : 'primary'}
                        onClick={() => setEditMode(!editMode)}
                    >
                        {editMode ? 'Stop Editing' : 'Edit Amenities'}
                    </Button>

                    {orderedTagKeys.map((tagKey) => (
                        <div key={tagKey} className="mb-3">
                            <h6 className="text-uppercase text-muted mt-2">
                                {tagKey === 'other' ? 'Other' : (tagLabelMap[tagKey] || tagKey)}
                            </h6>
                            {groupedByTag[tagKey].map((ba: any) => {
                                const amenity = amenityRef[ba.amenityId]
                                return (
                                    <div key={ba._id} className="mb-2 p-2 border border-1 rounded d-flex gap-3 align-items-center">
                                        {editMode && canAccess('routes', 'edit-building-amenity') && (
                                            <Button
                                                size="sm"
                                                disabled={isDeleting}
                                                variant="danger"
                                                onClick={() => handleRemoveAmenity(ba._id)}
                                            >
                                                X
                                            </Button>
                                        )}
                                        <div className="d-flex gap-2 align-items-center flex-wrap">
                                            <div className="fw-bold">
                                                {amenity?.title || ba.amenityId}
                                            </div>
                                            {amenity?.type && (
                                                <span className="small text-muted">{amenity.type}</span>
                                            )}
                                            {canAccess('routes', 'amenity') && amenity?._id && (
                                                <Link to={`/amenity/${amenity._id}`} className="small">
                                                    <BoxArrowUpRight size={14} viewBox={'0 0 20 20'} />
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    ))}

                    {editMode && canAccess('routes', 'edit-building-amenity') && (
                        <Button
                            className="mb-2"
                            size="sm"
                            disabled={isAdding}
                            variant="success"
                            onClick={() => setSelectModalActive(true)}
                        >
                            + Add Amenity
                        </Button>
                    )}
                </div>
            )}

            {buildingAmenityList.length === 0 && canAccess('routes', 'edit-building-amenity') && (
                <div className="building-amenities-list">
                    <h4>Building Amenities</h4>
                    <p className="text-muted">No amenities assigned to this building.</p>
                    <Button
                        className="mb-2"
                        size="sm"
                        disabled={isAdding}
                        variant="success"
                        onClick={() => setSelectModalActive(true)}
                    >
                        + Add Amenity
                    </Button>
                </div>
            )}

            {selectModalActive && (
                <SelectAmenityModal
                    active={selectModalActive}
                    onClose={() => setSelectModalActive(false)}
                    onSelect={handleAddAmenities}
                    title="Select Amenities"
                    message="Select amenities to add to this building"
                    multiSelect={true}
                    filterFn={(amenity: AmenityType) => !assignedAmenityIds.has(amenity._id || '')}
                />
            )}
        </>
    )
}
